import React from "react";
import { connect } from "react-redux";

import { faStickyNote } from "@fortawesome/pro-solid-svg-icons/faStickyNote";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover from "antd/es/popover";
import { useIntl } from "react-intl";

import { Activity } from "@mapmycustomers/shared/types/entity";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { stopEvents } from "@mapmycustomers/shared/util/browser";

import ActivityNotePreview from "@app/component/preview/components/ActivityNotePreview";
import { getCurrentUserId } from "@app/store/iam";
import { getTeamsOfEntireOrganization, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";

import styles from "./Note.module.scss";

export interface NoteProps {
  activity: Activity;
}

interface Props extends NoteProps {
  currentUserId: User["id"];
  teams: Team[];
  users: User[];
}

const Note: React.FC<Props> = ({ activity, currentUserId, teams, users }) => {
  const intl = useIntl();

  if (!activity.note) {
    // Wrapping into fragment because this component can't return a plain string.
    // On the other hand it can't return `null` because that's an AgGrid recommendation
    // to return empty strings: https://www.ag-grid.com/react-hooks/#react-null
    return <>{""}</>;
  }

  return (
    <Popover
      content={
        <ActivityNotePreview
          activity={activity}
          className={styles.paragraph}
          currentUserId={currentUserId}
          note={activity.note}
          teams={teams}
          users={users}
        />
      }
      destroyTooltipOnHide
      placement="bottomLeft"
      trigger={["hover"]}
    >
      <div className={styles.container} onClick={stopEvents}>
        <FontAwesomeIcon className={styles.icon} icon={faStickyNote} />
        <span className={styles.label}>
          {intl.formatMessage({
            id: "grid.noteField",
            defaultMessage: "Note",
            description: "Label on the Note chip",
          })}
        </span>
      </div>
    </Popover>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUserId(state)!,
  teams: getTeamsOfEntireOrganization(state),
  users: getUsers(state),
});

export default connect(mapStateToProps)(Note);
